import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Loader2, Sparkles, RefreshCcw } from "lucide-react";
import { useI18n, useLocalizedPath } from "../i18n/I18nContext";
import { api, formatApiError } from "../lib/api";
import { TID } from "../constants/testIds";
import Icon from "../components/Icon";

const ASSESS_BG = "https://images.pexels.com/photos/6141905/pexels-photo-6141905.jpeg";

export default function Assessment() {
  const { t, lang } = useI18n();
  const lp = useLocalizedPath();

  const [phase, setPhase] = useState("intro"); // intro | quiz | loading | report
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [idx, setIdx] = useState(0);
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");
  const [cats, setCats] = useState([]);

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCats(data)).catch(() => {});
  }, []);

  useEffect(() => {
    api
      .get("/assessment/questions", { params: { lang } })
      .then(({ data }) => {
        setQuestions(data.questions);
        setAnswers((prev) => (prev.length === data.questions.length ? prev : Array(data.questions.length).fill("")));
      })
      .catch(() => {});
  }, [lang]);

  const start = () => {
    setPhase("quiz");
    setIdx(0);
    setReport(null);
    setError("");
  };

  const setAnswer = (val) => {
    const next = [...answers];
    next[idx] = val;
    setAnswers(next);
  };

  const next = () => {
    if (idx < questions.length - 1) setIdx(idx + 1);
    else submit();
  };

  const submit = async () => {
    setPhase("loading");
    setError("");
    try {
      const { data } = await api.post("/assessment", { answers, language: lang });
      setReport(data);
      setPhase("report");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
      setPhase("quiz");
    }
  };

  return (
    <div className="relative">
      <div
        className="absolute inset-0 -z-10 opacity-20 pointer-events-none"
        style={{
          backgroundImage: `radial-gradient(50% 40% at 50% 0%, rgba(212,175,55,0.20), transparent 70%), url(${ASSESS_BG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          maskImage: "linear-gradient(180deg, black 0%, transparent 80%)",
          WebkitMaskImage: "linear-gradient(180deg, black 0%, transparent 80%)",
        }}
      />
      <div className="container mx-auto px-6 py-16 md:py-24 max-w-3xl">
        <AnimatePresence mode="wait">
          {phase === "intro" && (
            <motion.div
              key="intro"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              <p className="font-mono-label text-gold/80 mb-6 inline-flex items-center gap-2">
                <Sparkles className="h-3 w-3" />
                {lang === "en" ? "Personal · 3 minutes" : "Personal · 3 minute"}
              </p>
              <h1 className="font-display text-4xl md:text-6xl tracking-tighter">{t.assessment.title}</h1>
              <p className="mt-8 text-white/60 text-lg max-w-xl mx-auto">{t.assessment.sub}</p>
              <button
                data-testid={TID.assessmentStart}
                onClick={start}
                className="btn-gold mt-12 inline-flex items-center gap-2 group"
              >
                {t.assessment.start}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
            </motion.div>
          )}

          {phase === "quiz" && questions.length > 0 && (
            <motion.div
              key="quiz"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex items-center justify-between mb-6">
                <span className="font-mono-label text-white/40">
                  {t.assessment.question} {idx + 1} {t.assessment.of} {questions.length}
                </span>
                <div className="flex-1 mx-6 h-px bg-white/10 relative">
                  <motion.div
                    className="absolute inset-y-0 left-0 bg-gold"
                    initial={false}
                    animate={{ width: `${((idx + 1) / questions.length) * 100}%` }}
                    transition={{ duration: 0.4 }}
                  />
                </div>
                <span className="font-mono-label text-gold/80">{Math.round(((idx + 1) / questions.length) * 100)}%</span>
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: 24 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -24 }}
                  transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                  className="glass rounded-3xl p-8 md:p-12"
                >
                  <h2 className="font-display text-2xl md:text-3xl tracking-tight leading-snug">
                    {questions[idx]}
                  </h2>
                  <textarea
                    data-testid={TID.assessmentInput(idx)}
                    value={answers[idx] || ""}
                    onChange={(e) => setAnswer(e.target.value)}
                    placeholder={t.assessment.placeholder}
                    rows={5}
                    autoFocus
                    className="mt-8 w-full bg-obsidian-700 border border-white/10 rounded-2xl p-5 text-white placeholder-white/30 focus:outline-none focus:border-gold/60 transition-colors resize-none"
                  />
                </motion.div>
              </AnimatePresence>

              {error && (
                <p className="mt-4 text-sm text-red-300/90">{error}</p>
              )}

              <div className="mt-8 flex items-center justify-between">
                <button
                  data-testid={TID.assessmentPrev}
                  onClick={() => idx > 0 && setIdx(idx - 1)}
                  disabled={idx === 0}
                  className="btn-ghost inline-flex items-center gap-2 disabled:opacity-30"
                >
                  <ArrowLeft className="h-4 w-4" /> {t.assessment.prev}
                </button>
                <button
                  data-testid={idx === questions.length - 1 ? TID.assessmentSubmit : TID.assessmentNext}
                  onClick={next}
                  disabled={!(answers[idx] && answers[idx].trim().length > 0)}
                  className="btn-gold inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {idx === questions.length - 1 ? t.assessment.submit : t.assessment.next}
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </motion.div>
          )}

          {phase === "loading" && (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-center py-32"
            >
              <Loader2 className="h-10 w-10 animate-spin text-gold mx-auto" />
              <p className="mt-8 ed-italic text-2xl text-white/70">{t.assessment.loading}</p>
            </motion.div>
          )}

          {phase === "report" && report && (
            <motion.div
              key="report"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              data-testid={TID.assessmentReport}
            >
              <p className="font-mono-label text-gold/80 mb-4">{t.assessment.reportTitle}</p>
              <h1 className="font-display text-3xl md:text-5xl tracking-tighter leading-tight">
                {lang === "en" ? "Your reading is in." : "Lectura ta este gata."}
              </h1>

              <div className="glass rounded-3xl p-8 md:p-10 mt-10">
                <p className="ed-italic text-2xl md:text-3xl leading-snug text-white/90">
                  "{report.summary}"
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                <ReportList label={t.assessment.strengths} items={report.strengths} accent="text-gold" />
                <ReportList label={t.assessment.growth} items={report.growth_areas} accent="text-white" />
              </div>

              <div className="mt-12">
                <p className="font-mono-label text-gold/80 mb-6">{t.assessment.nextChapter}</p>
                <p className="ed-italic text-xl md:text-2xl text-white/85 leading-relaxed border-l-2 border-gold/40 pl-6">
                  {report.next_chapter}
                </p>
              </div>

              <div className="mt-16">
                <p className="font-mono-label text-gold/80 mb-6">{t.assessment.recommended}</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {report.recommended_categories.map((slug) => {
                    const c = cats.find((x) => x.slug === slug);
                    if (!c) return null;
                    return (
                      <Link
                        key={slug}
                        to={lp(`/guides/${slug}`)}
                        className="glass glass-hover rounded-2xl p-6 group block"
                      >
                        <div className="h-9 w-9 rounded-lg bg-gold/10 border border-gold/20 flex items-center justify-center mb-4">
                          <Icon name={c.icon} className="h-4 w-4 text-gold" />
                        </div>
                        <h3 className="font-display text-lg">{lang === "en" ? c.name_en : c.name_ro}</h3>
                        <span className="mt-4 text-xs font-mono-label text-white/40 group-hover:text-gold inline-flex items-center gap-1">
                          {lang === "en" ? "Open guide" : "Deschide ghidul"}
                          <ArrowRight className="h-3 w-3" />
                        </span>
                      </Link>
                    );
                  })}
                </div>
              </div>

              <div className="mt-16 flex flex-wrap gap-4">
                <button onClick={start} className="btn-ghost inline-flex items-center gap-2">
                  <RefreshCcw className="h-4 w-4" /> {t.assessment.retake}
                </button>
                <Link to={lp("/guides")} className="btn-gold inline-flex items-center gap-2">
                  {t.home.ctaSecondary} <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

function ReportList({ label, items, accent }) {
  return (
    <div className="glass rounded-2xl p-6">
      <p className="font-mono-label text-white/40 mb-4">{label}</p>
      <ul className="space-y-3">
        {items.map((it, i) => (
          <li key={i} className="flex items-start gap-3">
            <span className={`mt-1 h-1.5 w-1.5 rounded-full ${accent === "text-gold" ? "bg-gold" : "bg-white/60"}`} />
            <span className="text-white/85">{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
